import React, { useState } from 'react';
import { CreditCard, CheckCircle2, Download, ChevronRight, Zap } from 'lucide-react';
import { PageHeader, PageShell, SectionCard, Badge } from '../components/PeecUI';

interface BillingPageProps {
  workspaceId: string;
}

const invoices = [
  { id: 'INV-2026-06', date: '2026-06-01', amount: '¥2,980', status: 'paid', desc: '专业版 · 2026年6月' },
  { id: 'INV-2026-05', date: '2026-05-01', amount: '¥2,980', status: 'paid', desc: '专业版 · 2026年5月' },
  { id: 'INV-2026-04', date: '2026-04-01', amount: '¥2,980', status: 'paid', desc: '专业版 · 2026年4月' },
  { id: 'INV-2026-03', date: '2026-03-01', amount: '¥2,980', status: 'paid', desc: '专业版 · 2026年3月' },
];

const plans = [
  {
    name: '入门版', price: '¥980', per: '/月', features: ['5 个项目', '20 个提示词监控', '5 个 AI 平台', '每日采集'],
    current: false, highlight: false,
  },
  {
    name: '专业版', price: '¥2,980', per: '/月', features: ['10 个项目', '50 个提示词监控', '全部 AI 平台', '实时采集', '竞品对比报告', 'API 访问'],
    current: true, highlight: true,
  },
  {
    name: '企业版', price: '联系销售', per: '', features: ['无限项目', '无限提示词', '专属客户经理', 'SLA 保障', '私有部署选项', '自定义集成'],
    current: false, highlight: false,
  },
];

const BillingPage: React.FC<BillingPageProps> = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  return (
    <PageShell>
      <PageHeader title="订阅与账单" description="管理您的订阅计划、付款方式和账单记录" />

      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-slate-900">当前计划：专业版</span>
            <Badge tone="green">活跃</Badge>
          </div>
          <div className="text-xs text-slate-500">下次账单日：2026-07-01 · 金额 ¥2,980</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-sm text-slate-600">
            <CreditCard size={15} className="text-slate-400" />
            <span>**** 4521</span>
          </div>
          <button className="text-xs font-medium text-violet-600 hover:text-violet-800 flex items-center gap-0.5">
            更换卡片 <ChevronRight size={12} />
          </button>
        </div>
      </div>

      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">选择计划</h2>
        <div className="inline-flex items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-xs">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`rounded-md px-3 py-1.5 font-medium transition-colors ${billingCycle === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            按月
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`rounded-md px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${billingCycle === 'yearly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            按年 <span className="text-emerald-600 font-semibold">省20%</span>
          </button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3 mb-6">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`rounded-xl border p-5 ${plan.highlight ? 'border-violet-300 bg-violet-50/40 shadow-md' : 'border-slate-200 bg-white shadow-sm'}`}
          >
            {plan.current && (
              <div className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 bg-violet-100 rounded-full px-2 py-0.5 mb-3">
                <Zap size={10} /> 当前计划
              </div>
            )}
            <div className="font-bold text-slate-950 mb-0.5">{plan.name}</div>
            <div className="flex items-end gap-1 mb-4">
              <span className="text-2xl font-bold text-slate-950">{plan.price}</span>
              <span className="text-xs text-slate-400 mb-0.5">{plan.per}</span>
            </div>
            <ul className="space-y-2 mb-4">
              {plan.features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-xs text-slate-600">
                  <CheckCircle2 size={13} className={plan.highlight ? 'text-violet-500' : 'text-slate-400'} />
                  {f}
                </li>
              ))}
            </ul>
            <button
              className={`w-full rounded-lg py-2 text-sm font-medium transition-colors ${
                plan.current
                  ? 'bg-violet-600 text-white hover:bg-violet-700'
                  : plan.price === '联系销售'
                    ? 'border border-slate-200 text-slate-700 hover:bg-slate-50'
                    : 'border border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              {plan.current ? '管理计划' : plan.price === '联系销售' ? '联系我们' : '升级'}
            </button>
          </div>
        ))}
      </div>

      <SectionCard title="账单记录" subtitle="过去 12 个月的付款历史">
        <div className="divide-y divide-slate-100">
          {invoices.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between p-4 hover:bg-slate-50/70 transition-colors">
              <div>
                <div className="text-sm font-medium text-slate-800">{inv.desc}</div>
                <div className="text-xs text-slate-400">{inv.id} · {inv.date}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold text-slate-900">{inv.amount}</span>
                <Badge tone="green">已付款</Badge>
                <button aria-label="下载发票" className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors focus-visible:ring-2 focus-visible:ring-violet-400 outline-none">
                  <Download size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </PageShell>
  );
};

export default BillingPage;
